/* eslint-disable no-underscore-dangle */
import { useForm } from 'react-hook-form';
import { FormValues } from 'components/ScorecardDetails/interfaces';
import {
  formatScorecardData,
  parseScorecardData,
  getScorecardStates,
  isFormDirty,
  isOwnerScorecardEditingStatus,
} from 'helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import commonMessages from 'i18n/common';
import { patchScorecard, selectListData } from 'store/scorecards';
import { useToast, usePagePermissions, usePrompt } from 'hooks';
import { useUserRoles } from 'hooks/users';
import { Scorecard, ScorecardStores } from 'store/interfaces';
import { commonTexts } from 'i18n';
import { useAppDispatch } from 'store';
import { useSelector } from 'react-redux';

export default function useScorecardFormValues(
  scorecard: Scorecard,
  store: ScorecardStores,
  onSaved?: () => void,
) {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { goals, id, status } = scorecard;
  const { version: initialVersion } =
    useSelector(selectListData(store, id)) || {};
  const { version: userScorecardVersion } =
    useSelector(selectListData('userScorecards', id)) || {};

  const version = initialVersion || userScorecardVersion;

  const { isActive, isFeedbackCollected, isCompleted } =
    getScorecardStates(status);

  const form = useForm<FormValues>({
    defaultValues: {
      goals: parseScorecardData(goals),
      id,
      totalWeight: 0,
      entity: 'scorecard',
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    formState,
  } = form;
  // Gate on dirtyFields too, not just formState.isDirty: tab switches and the
  // server-weight sync useEffect below can flip the global isDirty flag even
  // when the user hasn't actually edited anything (RHF v7 recomputes the flag
  // by comparing values to defaults on register/setValue, which is fuzzy with
  // `string | number` weight typings). `dirtyFields` only fills when the user
  // types or when we explicitly setValue(.., { shouldDirty: true }) — see
  // `handleAddGoal` in EditScorecard.component.tsx and the matching
  // append/remove handlers in RegularGoalEditor / MetricGoalEditor.
  const isDirty = isFormDirty(formState);

  usePrompt(formatMessage(commonTexts.unsavedChangesPrompt), isDirty);

  useEffect(() => {
    goals.forEach(({ weight }, index) => {
      setValue(`goals.${index}.weight`, weight);
    });
  }, [setValue, goals]);
  const goalsFields = watch('goals');
  const showMessage = useToast();
  const goalErrorList = errors.goals as unknown as
    | Array<Record<string, unknown> | undefined>
    | undefined;
  const hasTopLevelBlockingError = Object.keys(errors).some(
    (key) => key !== 'totalWeight' && key !== 'goals',
  );
  const hasGoalBlockingError =
    Array.isArray(goalErrorList) &&
    goalErrorList.some(
      (goalError) =>
        Boolean(goalError) &&
        Object.keys(goalError as Record<string, unknown>).some(
          (key) => key !== 'subgoalsWeight',
        ),
    );
  const canSubmit =
    isDirty &&
    goalsFields?.length &&
    !hasTopLevelBlockingError &&
    !hasGoalBlockingError;
  const { isOwnScorecardsPage } = usePagePermissions();
  const withRestrictions =
    isOwnScorecardsPage || isFeedbackCollected || isCompleted;

  const isActiveOwnScorecard =
    isOwnScorecardsPage && isOwnerScorecardEditingStatus(status);

  const { isRevOpsManager, isReviewCycleManager } = useUserRoles();
  const canAddGoals =
    !isOwnScorecardsPage || !(isFeedbackCollected || isCompleted);
  const hasRestrictionForTemplateGoal = isOwnScorecardsPage || !canAddGoals;
  const metricEditContext = useMemo(
    () => ({
      hasRestrictionForTemplateGoal,
      isRevOpsManager: Boolean(isRevOpsManager),
      isReviewCycleManager: Boolean(isReviewCycleManager),
      canEditCuSatWeight: isOwnScorecardsPage && isActive,
    }),
    [
      hasRestrictionForTemplateGoal,
      isActive,
      isOwnScorecardsPage,
      isRevOpsManager,
      isReviewCycleManager,
    ],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(
    async (data: FormValues) => {
      if (isSubmitting) return; // Prevent multiple submissions

      try {
        setIsSubmitting(true);
        const response = await dispatch(
          patchScorecard({
            id,
            store,
            data: formatScorecardData(
              data,
              version as number,
              withRestrictions,
              isActiveOwnScorecard,
              metricEditContext,
            ),
          }),
        ).unwrap();

        reset({
          goals: parseScorecardData(response.goals),
          id,
          totalWeight: 0,
          entity: 'scorecard',
        });

        showMessage({
          severity: 'success',
          message: formatMessage(commonMessages.changesSavedSuccessfully),
        });

        onSaved?.();
      } catch (error: any) {
        // Error handling
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      dispatch,
      id,
      store,
      version,
      withRestrictions,
      isActiveOwnScorecard,
      metricEditContext,
      showMessage,
      formatMessage,
      reset,
      isSubmitting,
      onSaved,
    ],
  );

  const onSubmitClick = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );
  return {
    onSubmitClick,
    form,
    canSubmit: !isSubmitting && canSubmit,
    isSubmitting,
  };
}
